/**
 * Class names for the v2 referral form's lavender palette (see the v2 design
 * handoff). Shared so every section, button and control reads the same.
 */

/**
 * Applied on the `<form>`: 44px fields, 14px/600 labels and the lavender focus
 * ring for every input, select trigger and textarea inside, including the
 * composite pickers (date, phone, school, payer) that don't take a class.
 * The phone field's input and country button are squared off where they
 * meet. Opt a label out with `data-plain-label`.
 */
export const FORM_FIELD_STYLES = [
  "[&_input:not([type=checkbox]):not([type=radio])]:h-11",
  "[&_input:not([type=checkbox]):not([type=radio])]:border-input",
  "[&_input:not([type=checkbox]):not([type=radio])]:px-3.5",
  "[&_input:not([type=checkbox]):not([type=radio])]:text-[15px]",
  "[&_input:not([type=checkbox]):not([type=radio]):not([type=tel])]:rounded-[10px]",
  "[&_input[type=tel]]:rounded-e-[10px]",
  "[&_input[type=tel]]:rounded-s-none",
  "[&_.PhoneInput_button]:h-11",
  "[&_.PhoneInput_button]:rounded-s-[10px]",
  "[&_.PhoneInput_button]:border-input",
  "[&_button[role=combobox]]:h-11",
  "[&_button[role=combobox]]:rounded-[10px]",
  "[&_button[role=combobox]]:border-input",
  "[&_button[role=combobox]]:text-[15px]",
  "[&_textarea]:rounded-[10px]",
  "[&_textarea]:border-input",
  "[&_textarea]:text-[15px]",
  "[&_input:focus-visible]:border-primary",
  "[&_input:focus-visible]:ring-[3px]",
  "[&_input:focus-visible]:ring-primary/20",
  "[&_input:focus-visible]:ring-offset-0",
  "[&_textarea:focus-visible]:border-primary",
  "[&_textarea:focus-visible]:ring-[3px]",
  "[&_textarea:focus-visible]:ring-primary/20",
  "[&_textarea:focus-visible]:ring-offset-0",
  "[&_button[role=combobox]:focus]:border-primary",
  "[&_button[role=combobox]:focus]:ring-[3px]",
  "[&_button[role=combobox]:focus]:ring-primary/20",
  "[&_button[role=combobox]:focus]:ring-offset-0",
  "[&_label:not([data-plain-label])]:text-sm",
  "[&_label:not([data-plain-label])]:font-semibold",
].join(" ");

/** Section and sub-card containers. */
export const SECTION_CARD_CLASS =
  "rounded-2xl border-[1.5px] border-primary-hairline bg-white";
export const SUB_CARD_CLASS =
  "rounded-[14px] border border-[#e9e7f4] p-3 md:p-4";
export const SUB_CARD_LABEL_CLASS =
  "text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground";
/** A reveal nested under the control that opened it. */
export const NESTED_REVEAL_CLASS =
  "border-l-2 border-primary-hairline pl-[18px]";

/** Solid lavender buttons; no gradients. */
export const PINK_BUTTON_CLASS =
  "rounded-xl md:rounded-xl border border-primary-soft-border bg-primary-soft text-sm font-medium text-foreground hover:bg-primary-soft-hover focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";
export const PINK_BUTTON_DISABLED_CLASS =
  "disabled:border-[#e3e7e9] disabled:bg-[#f1f3f4] disabled:text-muted-foreground disabled:opacity-100 disabled:cursor-not-allowed";
export const GHOST_BUTTON_CLASS =
  "rounded-xl md:rounded-xl border border-primary-hairline bg-white text-sm font-medium text-foreground hover:bg-primary-subtle";

/** Segmented choices ("Pink fill"). */
export const SEGMENT_CLASS =
  "h-auto rounded-[10px] md:rounded-[10px] border px-4 py-2.5 text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";
export const SEGMENT_SELECTED_CLASS =
  "border-primary-soft-border bg-primary-soft font-semibold text-foreground";
export const SEGMENT_UNSELECTED_CLASS =
  "border-input bg-white font-medium text-foreground hover:border-primary-soft-border";

/**
 * Alerts and callouts, from the design system's status tokens (see
 * `FormNotice`). Inline text with a bold lead-in, no icon.
 */
export const NOTICE_TONE_CLASSES = {
  error: "border-error-border bg-error-subtle text-error",
  warning: "border-warning-border bg-warning-subtle text-warning",
  info: "border-info-border bg-info-subtle text-info",
  success: "border-success-border bg-success-subtle text-success",
} as const;
/** Blocking errors and dismissible warnings. */
export const NOTICE_ALERT_CLASS =
  "rounded-[10px] border px-3.5 py-3 text-[13px] leading-[1.5]";
/** Non-blocking notes and info (out-of-network plan, sponsorship). */
export const NOTICE_NOTE_CLASS =
  "rounded-[9px] border px-[13px] py-[11px] text-[12.5px] leading-[1.5]";
export const NOTICE_LINK_CLASS =
  "[&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2";
/** The 911/988 strip at the top of the form. */
export const EMERGENCY_STRIP_CLASS =
  "rounded-r-lg border-l-[3px] border-warning-border bg-warning-subtle px-3.5 py-[9px] text-[12.5px] leading-[1.5] text-warning";
/** Field-level and Continue errors. */
export const ERROR_TEXT_CLASS = "text-[13px] text-error";

/** Round checkboxes: lavender fill and white check when on. */
export const PINK_CHECKBOX_CLASS =
  "border-[1.5px] border-input data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-white focus-visible:ring-primary";
